/**
 * useStories Composable - REBUILT
 * Clean architecture for story viewer
 *
 * RESPONSIBILITIES:
 * 1. State management (indices, progress, pause state)
 * 2. Timer control (auto-advance, progress animation)
 * 3. Navigation (next/prev status/group)
 * 4. Playback control (pause/resume)
 *
 * DOES NOT HANDLE:
 * - UI events (handled by component)
 * - Input focus logic (handled by component)
 */

import { ref, computed, onMounted, onUnmounted } from "vue";
import { useStoriesStore } from "@/stores/stories";

const DEFAULT_DURATION = 5000;

export function useStories(initialGroupIndex = 0, onClose = null) {
  const store = useStoriesStore();

  // ═══════════════════════════════════════════════════════════
  // STATE
  // ═══════════════════════════════════════════════════════════

  // Indices - initialized immediately with passed values
  const currentGroupIndex = ref(Math.max(0, initialGroupIndex));
  const currentStatusIndex = ref(0);

  // Playback state
  const isPaused = ref(false);
  const isContentLoaded = ref(false);
  const progress = ref(0);
  const currentDuration = ref(DEFAULT_DURATION);

  // UI state
  const showPauseIcon = ref(false);

  // Drag state
  const dragOffset = ref(0);
  const dragScale = ref(1);
  const isDragging = ref(false);

  // Video reference
  const videoRef = ref(null);

  // Timer references (private)
  let autoAdvanceTimer = null;
  let progressInterval = null;
  let pauseIconTimer = null;
  let statusStartTime = null;
  let elapsedBeforePause = 0;

  // ═══════════════════════════════════════════════════════════
  // COMPUTED
  // ═══════════════════════════════════════════════════════════

  const statusGroups = computed(() => store.statusGroups || []);

  const currentGroup = computed(() => {
    const groups = statusGroups.value;
    const idx = currentGroupIndex.value;
    if (idx < 0 || idx >= groups.length) return null;
    return groups[idx];
  });

  const currentStatus = computed(() => {
    const group = currentGroup.value;
    if (!group || !group.statuses) return null;
    const idx = currentStatusIndex.value;
    if (idx < 0 || idx >= group.statuses.length) return null;
    return group.statuses[idx];
  });

  const totalStatusesInGroup = computed(() => {
    return currentGroup.value?.statuses?.length || 0;
  });

  const hasNextStatus = computed(() => {
    return currentStatusIndex.value < totalStatusesInGroup.value - 1;
  });

  const hasPreviousStatus = computed(() => {
    return currentStatusIndex.value > 0;
  });

  const hasNextGroup = computed(() => {
    return currentGroupIndex.value < statusGroups.value.length - 1;
  });

  const hasPreviousGroup = computed(() => {
    return currentGroupIndex.value > 0;
  });

  // ═══════════════════════════════════════════════════════════
  // TIMER CONTROL
  // ═══════════════════════════════════════════════════════════

  function clearAllTimers() {
    if (autoAdvanceTimer) {
      clearTimeout(autoAdvanceTimer);
      autoAdvanceTimer = null;
    }
    if (progressInterval) {
      clearInterval(progressInterval);
      progressInterval = null;
    }
    if (pauseIconTimer) {
      clearTimeout(pauseIconTimer);
      pauseIconTimer = null;
    }
  }

  function startTimer(duration) {
    if (isPaused.value) return;

    clearAllTimers();
    statusStartTime = Date.now();
    elapsedBeforePause = 0;

    // Progress animation
    progressInterval = setInterval(() => {
      if (isPaused.value) return;
      const elapsed = Date.now() - statusStartTime + elapsedBeforePause;
      progress.value = Math.min(elapsed / duration, 1);
    }, 50);

    // Auto-advance
    autoAdvanceTimer = setTimeout(() => {
      progress.value = 1;
      goToNextStatus();
    }, duration);
  }

  // ═══════════════════════════════════════════════════════════
  // STATUS INITIALIZATION
  // ═══════════════════════════════════════════════════════════

  function initializeStatus() {
    clearAllTimers();
    isContentLoaded.value = false;
    progress.value = 0;
    elapsedBeforePause = 0;
    currentDuration.value = DEFAULT_DURATION;

    const status = currentStatus.value;
    if (!status) return;

    // Mark as viewed
    store.markAsViewed(status.id);

    // Text statuses are immediately ready
    if (status.type === "text") {
      isContentLoaded.value = true;
      if (!isPaused.value) {
        startTimer(DEFAULT_DURATION);
      }
    }
  }

  function onContentLoaded(duration = DEFAULT_DURATION) {
    if (isContentLoaded.value) return;

    isContentLoaded.value = true;
    currentDuration.value = duration;

    if (!isPaused.value) {
      startTimer(duration);
    }
  }

  // ═══════════════════════════════════════════════════════════
  // NAVIGATION
  // ═══════════════════════════════════════════════════════════

  function goToNextStatus() {
    if (hasNextStatus.value) {
      currentStatusIndex.value++;
      initializeStatus();
    } else if (hasNextGroup.value) {
      currentGroupIndex.value++;
      currentStatusIndex.value = 0;
      initializeStatus();
    } else if (onClose) {
      onClose();
    }
  }

  function goToPreviousStatus() {
    if (hasPreviousStatus.value) {
      currentStatusIndex.value--;
      initializeStatus();
    } else if (hasPreviousGroup.value) {
      currentGroupIndex.value--;
      const prevGroup = statusGroups.value[currentGroupIndex.value];
      currentStatusIndex.value = (prevGroup?.statuses?.length || 1) - 1;
      initializeStatus();
    }
    // At first status of first group - do nothing
  }

  // ═══════════════════════════════════════════════════════════
  // PLAYBACK CONTROL
  // ═══════════════════════════════════════════════════════════

  function pausePlayback() {
    if (isPaused.value) return;

    // Save elapsed time
    if (statusStartTime) {
      elapsedBeforePause += Date.now() - statusStartTime;
    }

    isPaused.value = true;
    showPauseIcon.value = true;

    // Stop timers
    if (autoAdvanceTimer) {
      clearTimeout(autoAdvanceTimer);
      autoAdvanceTimer = null;
    }

    // Pause video
    if (videoRef.value) {
      videoRef.value.pause();
    }

    // Hide pause icon after delay
    pauseIconTimer = setTimeout(() => {
      showPauseIcon.value = false;
    }, 800);
  }

  function resumePlayback() {
    if (!isPaused.value) return;

    if (pauseIconTimer) {
      clearTimeout(pauseIconTimer);
      pauseIconTimer = null;
    }

    isPaused.value = false;
    showPauseIcon.value = false;

    // Resume video
    if (videoRef.value) {
      videoRef.value.play();
    }

    // Calculate remaining time
    let remaining;
    if (videoRef.value && videoRef.value.duration) {
      remaining = (videoRef.value.duration - videoRef.value.currentTime) * 1000;
    } else {
      remaining = Math.max(100, currentDuration.value - elapsedBeforePause);
    }

    // Restart timer
    statusStartTime = Date.now();

    if (progressInterval) clearInterval(progressInterval);
    progressInterval = setInterval(() => {
      if (isPaused.value) return;
      const elapsed = Date.now() - statusStartTime + elapsedBeforePause;
      progress.value = Math.min(elapsed / currentDuration.value, 1);
    }, 50);

    autoAdvanceTimer = setTimeout(() => {
      progress.value = 1;
      goToNextStatus();
    }, remaining);
  }

  // ═══════════════════════════════════════════════════════════
  // DRAG TO DISMISS
  // ═══════════════════════════════════════════════════════════

  function onDragStart() {
    isDragging.value = true;
    pausePlayback();
  }

  function onDragUpdate(deltaY) {
    if (deltaY > 0 || dragOffset.value > 0) {
      dragOffset.value = Math.max(0, Math.min(dragOffset.value + deltaY, 400));
      dragScale.value = 1.0 - (dragOffset.value / 400) * 0.2;
    }
  }

  function onDragEnd(velocity = 0) {
    const shouldDismiss = dragOffset.value > 100 || velocity > 500;
    if (!shouldDismiss) {
      dragOffset.value = 0;
      dragScale.value = 1;
      isDragging.value = false;
      resumePlayback();
    }
    return shouldDismiss;
  }

  // ═══════════════════════════════════════════════════════════
  // VIDEO CONTROL
  // ═══════════════════════════════════════════════════════════

  function setVideoRef(el) {
    videoRef.value = el;
  }

  // ═══════════════════════════════════════════════════════════
  // LIFECYCLE
  // ═══════════════════════════════════════════════════════════

  function handleVisibilityChange() {
    if (document.hidden) {
      pausePlayback();
    }
  }

  onMounted(() => {
    document.addEventListener("visibilitychange", handleVisibilityChange);
    // Start from index 0 of the selected group
    currentStatusIndex.value = 0;
    initializeStatus();
  });

  onUnmounted(() => {
    clearAllTimers();
    document.removeEventListener("visibilitychange", handleVisibilityChange);
  });

  // ═══════════════════════════════════════════════════════════
  // PUBLIC API
  // ═══════════════════════════════════════════════════════════

  return {
    // State (readonly for component)
    currentGroupIndex,
    currentStatusIndex,
    isPaused,
    isContentLoaded,
    showPauseIcon,
    progress,
    dragOffset,
    dragScale,
    isDragging,

    // Computed
    currentGroup,
    currentStatus,
    totalStatusesInGroup,

    // Navigation
    goToNextStatus,
    goToPreviousStatus,

    // Playback
    pausePlayback,
    resumePlayback,

    // Content
    onContentLoaded,
    setVideoRef,

    // Drag
    onDragStart,
    onDragUpdate,
    onDragEnd,
  };
}
